import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import RequiredMark from '@/temu-platform/components/RequiredMark';
import CategoryCascaderSelect from '@/temu-platform/components/CategoryCascaderSelect';
import TemuSwitch from '@/temu-platform/components/TemuSwitch';
import { flattenLeafCategories } from '@/temu-platform/utils/temuCategoryTree';
import { nextTemplatePriority } from '@/temu-platform/utils/temuTemplatePriority';
import {
  conflictToastMessage,
  isTemplateVersionConflictError,
  parseTemplateVersion,
} from '@/temu-platform/utils/temuTemplateVersionGuard';
import TemplateVersionConflictMark from '@/temu-platform/modules/products/components/TemplateVersionConflictMark';
import { temuPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';
import {
  normalizeRrpProof,
  toRrpProofPayload,
} from '@/temu-platform/modules/discuss/rrpDisplay';
import RrpProofUploadBlock from '@/temu-platform/modules/discuss/RrpProofUploadBlock';


function parseOptionalNumber(value) {
  const s = String(value ?? '').trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function emptyProofState() {
  return { attachments: [], proofLink: '' };
}

/**
 * 新建/编辑建议价资料库：类目 + 自动规则 + 加价% / 固定底价。
 */
export default function RrpTemplateCreateModal({
  open,
  shopId,
  templateId = null,
  onClose,
  onSaved,
}) {
  const { t } = useTranslation('workbench');
  const isEdit = Boolean(templateId);
  const [name, setName] = useState('');
  const [remark, setRemark] = useState('');
  const [autoRuleEnabled, setAutoRuleEnabled] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [productTypeId, setProductTypeId] = useState('');
  const [categoryPathLabel, setCategoryPathLabel] = useState('');
  const [markupPercent, setMarkupPercent] = useState('');
  const [fixedPrice, setFixedPrice] = useState('');
  const [typeOneProof, setTypeOneProof] = useState(emptyProofState);
  const [typeTwoProof, setTypeTwoProof] = useState(emptyProofState);
  const [saving, setSaving] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [metaLoading, setMetaLoading] = useState(false);
  const [templateVersion, setTemplateVersion] = useState(null);
  const [versionConflict, setVersionConflict] = useState(false);
  const [reloadingVersion, setReloadingVersion] = useState(false);

  const categoryOptions = useMemo(
    () => flattenLeafCategories(categories).map((opt) => ({
      value: opt.categoryId,
      label: opt.label,
      productTypeId: opt.productTypeId,
    })),
    [categories],
  );

  const applyItem = (item) => {
    const payload = item.payload && typeof item.payload === 'object' ? item.payload : {};
    setName(String(item.name || '').trim());
    setRemark(String(item.remark || '').trim());
    setAutoRuleEnabled(item.autoRuleEnabled === true || item.autoRuleEnabled === 1);
    setSortOrder(Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : 0);
    setProductTypeId(String(item.productTypeId || payload.productTypeId || ''));
    setCategoryId(String(item.categoryId || payload.categoryId || ''));
    setCategoryPathLabel(String(item.categoryPathLabel || payload.categoryPathLabel || ''));
    const markup = item.markupPercent ?? payload.markupPercent;
    setMarkupPercent(markup != null && markup !== '' ? String(markup) : '');
    const fixed = payload.fixedPrice ?? item.fixedPrice;
    setFixedPrice(fixed != null && fixed !== '' ? String(fixed) : '');
    const typeOne = normalizeRrpProof(payload.type_one_proof || payload.typeOneProof);
    const typeTwo = normalizeRrpProof(payload.type_two_proof || payload.typeTwoProof);
    setTypeOneProof({ attachments: typeOne.attachments, proofLink: typeOne.proof_link });
    setTypeTwoProof({ attachments: typeTwo.attachments, proofLink: typeTwo.proof_link });
    setTemplateVersion(parseTemplateVersion(item.version));
  };

  useEffect(() => {
    if (!open) return;
    setName('');
    setRemark('');
    setAutoRuleEnabled(false);
    setSortOrder(0);
    setCategoryId('');
    setProductTypeId('');
    setCategoryPathLabel('');
    setMarkupPercent('');
    setFixedPrice('');
    setTypeOneProof(emptyProofState());
    setTypeTwoProof(emptyProofState());
    setSaving(false);
    setDetailLoading(false);
    setTemplateVersion(null);
    setVersionConflict(false);
    setReloadingVersion(false);
  }, [open, templateId]);

  useEffect(() => {
    if (!open || !shopId) {
      setCategories([]);
      return undefined;
    }
    let cancelled = false;
    setMetaLoading(true);
    temuPlatformAPI.getShopCategories(shopId)
      .then((res) => {
        if (cancelled) return;
        setCategories(res?.categories ?? res?.items ?? res?.list ?? res ?? []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      })
      .finally(() => {
        if (!cancelled) setMetaLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId]);

  useEffect(() => {
    if (!open || !shopId || !templateId) return undefined;
    let cancelled = false;
    setDetailLoading(true);
    temuPlatformAPI.getRrpTemplate(shopId, templateId)
      .then((res) => {
        if (cancelled) return;
        applyItem(res?.item || res || {});
        setVersionConflict(false);
      })
      .catch((e) => {
        if (cancelled) return;
        showToast(e?.message || t('photoLib.loadTemplateFailed'), 'error');
        onClose?.();
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, templateId, onClose, t]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const busy = saving || detailLoading;

  const onCategoryChange = (id, meta) => {
    const nextCategoryId = id ? String(id) : '';
    const hit = categoryOptions.find((opt) => String(opt.value) === nextCategoryId);
    // 类目缓存只存一级，末级由级联懒加载返回，categoryOptions 里可能查不到 → 用 meta 兜底
    const metaTypeId = meta?.productTypeId != null && String(meta.productTypeId) !== '0'
      ? String(meta.productTypeId)
      : '';
    // TEMU 无独立商品类型，末级 catId 即 productTypeId
    const nextTypeId = hit?.productTypeId && String(hit.productTypeId) !== '0'
      ? String(hit.productTypeId)
      : (metaTypeId || nextCategoryId);
    setCategoryId(nextCategoryId);
    setProductTypeId(nextTypeId);
    setCategoryPathLabel(hit?.label || meta?.label || '');
    if (!isEdit && shopId && nextTypeId) {
      temuPlatformAPI.listRrpTemplates(shopId, {
        productTypeId: nextTypeId,
        page: 1,
        pageSize: 100,
      }).then((res) => {
        setSortOrder(nextTemplatePriority(res?.items));
      }).catch(() => {
        setSortOrder(0);
      });
    }
  };

  const reloadTemplateFromServer = async () => {
    if (!shopId || !templateId) return;
    setReloadingVersion(true);
    try {
      const res = await temuPlatformAPI.getRrpTemplate(shopId, templateId);
      applyItem(res?.item || res || {});
      setVersionConflict(false);
      showToast(t('rrpLib.reloaded'), 'success');
    } catch (e) {
      showToast(e?.message || t('photoLib.reloadFailed'), 'error');
    } finally {
      setReloadingVersion(false);
    }
  };

  const submit = async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'error');
      return;
    }
    const title = String(name || '').trim();
    if (!title) {
      showToast(t('photoLib.needName'), 'error');
      return;
    }
    if (!productTypeId) {
      showToast(t('rrpLib.needCategory'), 'error');
      return;
    }
    const markup = parseOptionalNumber(markupPercent);
    if (markup == null) {
      showToast(t('rrpLib.needMarkup'), 'error');
      return;
    }
    if (markup < 0 || markup > 1000) {
      showToast(t('rrpLib.markupRange'), 'error');
      return;
    }
    const fixed = parseOptionalNumber(fixedPrice);
    if (String(fixedPrice || '').trim() && fixed == null) {
      showToast(t('rrpLib.invalidFloor'), 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        markupPercent: markup,
        type_one_proof: toRrpProofPayload(typeOneProof.attachments, typeOneProof.proofLink),
        type_two_proof: toRrpProofPayload(typeTwoProof.attachments, typeTwoProof.proofLink),
      };
      if (fixed != null) payload.fixedPrice = fixed;
      const body = {
        name: title,
        remark: String(remark || '').trim() || undefined,
        productTypeId,
        categoryId,
        categoryPathLabel,
        autoRuleEnabled,
        sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
        markupPercent: markup,
        payload,
      };
      if (isEdit && templateVersion != null) {
        body.version = templateVersion;
      }
      const res = isEdit
        ? await temuPlatformAPI.updateRrpTemplate(shopId, templateId, body)
        : await temuPlatformAPI.createRrpTemplate(shopId, body);
      if (res?.item && parseTemplateVersion(res.item.version) != null) {
        setTemplateVersion(parseTemplateVersion(res.item.version));
      }
      setVersionConflict(false);
      showToast(isEdit ? t('photoLib.updated') : t('photoLib.createdTpl'), 'success');
      onSaved?.();
      onClose?.();
    } catch (e) {
      if (isEdit && isTemplateVersionConflictError(e)) {
        showToast(conflictToastMessage([{ type: 'rrp' }]), 'warning', 3000);
        setVersionConflict(true);
      } else {
        showToast(e?.message || (isEdit ? t('photoLib.updateFailed') : t('photoLib.createFailed')), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="tpo-batch-modal tpo-cert-modal" role="presentation">
      <button
        type="button"
        className="tpo-batch-modal__backdrop"
        aria-label={t('moduleShell.close')}
        onClick={() => { if (!busy) onClose?.(); }}
      />
      <div
        className="tpo-batch-modal__panel tpo-cert-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-rrp-tpl-create-title"
      >
        <div className="tpo-batch-modal__head">
          <h2 id="tpo-rrp-tpl-create-title">{isEdit ? t('rrpLib.editLib') : t('rrpLib.createLib')}</h2>
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={onClose} disabled={busy}>
            {t('moduleShell.close')}
          </button>
        </div>
        <div className="tpo-batch-modal__body tpo-cert-modal__body">
          {detailLoading ? (
            <p className="tpo-wizard__inline-hint">{t('photoLib.loadingTemplate')}</p>
          ) : null}
          <div className="tpo-wizard__form tpo-template-create-form">
            <label className={`tpo-field tpo-field--full${versionConflict ? ' tpo-field--version-conflict' : ''}`}>
              <span className="tpo-field__label">
                {t('productTemplate.colName')} <RequiredMark />
                <TemplateVersionConflictMark
                  active={versionConflict}
                  label={t('photoLib.updatedMark')}
                  reloading={reloadingVersion}
                  onReload={reloadTemplateFromServer}
                />
              </span>
              <input
                className="tpo-input"
                value={name}
                disabled={busy}
                maxLength={128}
                placeholder={t('rrpLib.namePh')}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="tpo-field tpo-field--full">
              <span className="tpo-field__label">{t('productTemplate.colRemark')}</span>
              <input
                className="tpo-input"
                value={remark}
                disabled={busy}
                maxLength={200}
                placeholder={t('photoLib.optional')}
                onChange={(e) => setRemark(e.target.value)}
              />
            </label>
            <label className="tpo-field tpo-field--full">
              <span className="tpo-field__label">{t('rrpLib.categoryApply')} <RequiredMark /></span>
              <CategoryCascaderSelect
                shopId={shopId}
                categories={categories}
                value={categoryId}
                label={categoryPathLabel}
                disabled={busy || metaLoading || !shopId}
                placeholder={
                  !shopId
                    ? t('returnList.pickShop')
                    : (metaLoading || detailLoading ? t('returnList.loading') : t('rrpLib.categorySearch'))
                }
                onChange={onCategoryChange}
              />
            </label>
            <div className="tpo-field tpo-field--full tpo-template-auto-rule">
              <div className="tpo-template-auto-rule__switch">
                <TemuSwitch
                  checked={autoRuleEnabled}
                  disabled={busy}
                  ariaLabel={t('rrpLib.autoRule')}
                  onChange={setAutoRuleEnabled}
                />
                <span>{t('rrpLib.autoRule')}</span>
              </div>
              <p className="tpo-wizard__inline-hint">
                {t('rrpLib.autoHintTemu')}
              </p>
              <label className="tpo-field">
                <span className="tpo-field__label">{t('rrpLib.priority')}</span>
                <input
                  className="tpo-input"
                  type="number"
                  step={1}
                  value={sortOrder}
                  disabled={busy}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setSortOrder(Number.isFinite(next) ? next : 0);
                  }}
                />
              </label>
            </div>
            <label className="tpo-field tpo-field--full">
              <span className="tpo-field__label">{t('rrpLib.markupLabel')} <RequiredMark /></span>
              <input
                className="tpo-input"
                type="number"
                min={0}
                max={1000}
                step="any"
                value={markupPercent}
                disabled={busy}
                placeholder="0～1000"
                onChange={(e) => setMarkupPercent(e.target.value)}
              />
              <span className="tpo-wizard__inline-hint">{t('rrpLib.markupHint')}</span>
            </label>
            <label className="tpo-field tpo-field--full">
              <span className="tpo-field__label">{t('rrpLib.floorLabel')}</span>
              <input
                className="tpo-input"
                type="number"
                min={0}
                step="any"
                value={fixedPrice}
                disabled={busy}
                placeholder={t('rrpLib.floorPh')}
                onChange={(e) => setFixedPrice(e.target.value)}
              />
            </label>
            <div className="tpo-field tpo-field--full tpo-rrp-tpl-proof-section">
              <span className="tpo-field__label">{t('rrpLib.proofLabel')}</span>
              <RrpProofUploadBlock
                title={t('rrpLib.proofType1')}
                shopId={shopId}
                busy={busy}
                attachments={typeOneProof.attachments}
                proofLink={typeOneProof.proofLink}
                onChange={setTypeOneProof}
              />
              <RrpProofUploadBlock
                title={t('rrpLib.proofType2')}
                shopId={shopId}
                busy={busy}
                attachments={typeTwoProof.attachments}
                proofLink={typeTwoProof.proofLink}
                onChange={setTypeTwoProof}
              />
            </div>
          </div>
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" onClick={onClose} disabled={busy}>
            {t('shop.cancel')}
          </button>
          <button
            type="button"
            className="tpo-btn tpo-btn--primary tpo-btn--sm"
            onClick={submit}
            disabled={busy || !shopId}
          >
            {saving ? t('tiktokPublish.saving') : (isEdit ? t('photoLib.saveChanges') : t('shop.save'))}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
